import { collection, getDocs, doc, getDoc, addDoc, setDoc, updateDoc, deleteDoc, query, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getCurrentMillis } from '../utils/dateUtils';
import type { LegalFile } from '../types';

const LEGAL_FILES_REF = 'LegalFiles';

const isFirebaseAvailable = () => {
  if (!db) {
    console.error('Firebase not available');
    return false;
  }
  return true;
};

export const getLegalFiles = async (): Promise<LegalFile[]> => {
  try {
    if (!isFirebaseAvailable()) {
      return [];
    }
    const legalFilesRef = collection(db, LEGAL_FILES_REF);
    const querySnapshot = await getDocs(legalFilesRef);
    
    const legalFiles: LegalFile[] = [];
    querySnapshot.forEach((doc) => {
      legalFiles.push({
        id: doc.id,
        ...doc.data()
      } as LegalFile);
    });
    
    return legalFiles;
  } catch (error) {
    console.error('Error fetching legal files:', error);
    return [];
  }
};

export const getLegalFileById = async (id: string): Promise<LegalFile | null> => {
  try {
    if (!isFirebaseAvailable()) {
      return null;
    }
    const legalFileRef = doc(db, LEGAL_FILES_REF, id);
    const legalFileDoc = await getDoc(legalFileRef);
    
    if (legalFileDoc.exists()) {
      return {
        id: legalFileDoc.id,
        ...legalFileDoc.data()
      } as LegalFile;
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching legal file:', error);
    return null;
  }
};

export const addLegalFile = async (legalFileData: Omit<LegalFile, 'id'>): Promise<string> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }
    const legalFilesRef = collection(db, LEGAL_FILES_REF);
    const docRef = await addDoc(legalFilesRef, legalFileData);
    return docRef.id;
  } catch (error) {
    console.error('Error adding legal file:', error);
    throw error;
  }
};

export const updateLegalFile = async (id: string, updates: Partial<LegalFile>): Promise<void> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }
    
    const legalFileRef = doc(db, LEGAL_FILES_REF, id);
    await updateDoc(legalFileRef, {
      ...updates,
      updatedAt: getCurrentMillis()
    });
  } catch (error) {
    console.error('Error updating legal file:', error);
    throw error;
  }
};

export const deleteLegalFile = async (id: string): Promise<void> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }
    
    const legalFileRef = doc(db, LEGAL_FILES_REF, id);
    await deleteDoc(legalFileRef);
  } catch (error) {
    console.error('Error deleting legal file:', error);
    throw error;
  }
};

export const getAttachmentsForLegalFile = async (legalFileId: string): Promise<any[]> => {
  try {
    if (!isFirebaseAvailable()) {
      return [];
    }
    const attachmentsRef = collection(db, LEGAL_FILES_REF, legalFileId, 'Attachments');
    const querySnapshot = await getDocs(attachmentsRef);
    
    const attachments: any[] = [];
    querySnapshot.forEach((doc) => {
      attachments.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return attachments;
  } catch (error) {
    console.error('Error fetching attachments for legal file:', error);
    return [];
  }
};

export const addAttachmentToLegalFile = async (
  legalFileId: string, 
  attachmentData: {
    attachmentURL: string;
    caseID: string;
    createdAt: string;
    createdBy: string;
    isArchived: boolean;
    isDeleted: boolean;
    lawyerID: string;
    notes: string;
    type: string;
    updatedAt: string;
    updatedBy: string;
    format: string; // Add format field
  },
  userId: string
): Promise<string> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }
    
    // Use createdAt as the document ID
    const attachmentId = attachmentData.createdAt;
    const attachmentRef = doc(db, LEGAL_FILES_REF, legalFileId, 'Attachments', attachmentId);
    
    // Add the ID to the attachment data
    const attachmentDataWithId = {
      ...attachmentData,
      id: attachmentId
    };
    
    await setDoc(attachmentRef, attachmentDataWithId);
    return attachmentId;
  } catch (error) {
    console.error('Error adding attachment to legal file:', error);
    throw error;
  }
};

export const deleteAttachment = async (legalFileId: string, attachmentId: string): Promise<void> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }
    
    const attachmentRef = doc(db, LEGAL_FILES_REF, legalFileId, 'Attachments', attachmentId);
    await deleteDoc(attachmentRef);
  } catch (error) {
    console.error('Error deleting attachment:', error);
    throw error;
  }
};

export const archiveLegalFile = async (id: string): Promise<void> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }
    const legalFileRef = doc(db, LEGAL_FILES_REF, id);
    await updateDoc(legalFileRef, {
      isArchived: true,
      updatedAt: getCurrentMillis()
    });
  } catch (error) {
    console.error('Error archiving legal file:', error);
    throw error;
  }
};

export const unarchiveLegalFile = async (id: string): Promise<void> => {
  try {
    if (!isFirebaseAvailable()) {
      throw new Error('Firebase not available');
    }
    const legalFileRef = doc(db, LEGAL_FILES_REF, id);
    await updateDoc(legalFileRef, {
      isArchived: false,
      updatedAt: getCurrentMillis()
    });
  } catch (error) {
    console.error('Error unarchiving legal file:', error);
    throw error;
  }
};

export const getLegalFilesByStatus = async (status: string): Promise<LegalFile[]> => {
  try {
    if (!isFirebaseAvailable()) {
      return [];
    }
    const legalFilesRef = collection(db, LEGAL_FILES_REF);
    const q = query(legalFilesRef, where('statusID', '==', status));
    const querySnapshot = await getDocs(q);
    
    const legalFiles: LegalFile[] = [];
    querySnapshot.forEach((doc) => {
      legalFiles.push({
        id: doc.id,
        ...doc.data()
      } as LegalFile);
    });
    
    return legalFiles;
  } catch (error) {
    console.error('Error fetching legal files by status:', error);
    return [];
  }
};

export const getLegalFilesByType = async (type: string): Promise<LegalFile[]> => {
  try {
    if (!isFirebaseAvailable()) {
      return [];
    }
    const legalFilesRef = collection(db, LEGAL_FILES_REF);
    const q = query(legalFilesRef, where('typeID', '==', type));
    const querySnapshot = await getDocs(q);
    
    const legalFiles: LegalFile[] = [];
    querySnapshot.forEach((doc) => {
      legalFiles.push({
        id: doc.id,
        ...doc.data()
      } as LegalFile);
    });
    
    return legalFiles;
  } catch (error) {
    console.error('Error fetching legal files by type:', error);
    return [];
  }
};

export const searchLegalFiles = async (searchTerm: string): Promise<LegalFile[]> => {
  try {
    if (!isFirebaseAvailable()) {
      return [];
    }
    const legalFilesRef = collection(db, LEGAL_FILES_REF);
    const querySnapshot = await getDocs(legalFilesRef);
    
    const legalFiles: LegalFile[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data() as LegalFile;
      if (data.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          data.caseNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          data.description?.toLowerCase().includes(searchTerm.toLowerCase())) {
        legalFiles.push({
          ...data,
          id: doc.id
        });
      }
    });
    
    return legalFiles;
  } catch (error) {
    console.error('Error searching legal files:', error);
    return [];
  }
};

export const getLegalFilesByLawyerID = async (lawyerID: string): Promise<LegalFile[]> => {
  try {
    if (!isFirebaseAvailable()) {
      return [];
    }
    const legalFilesRef = collection(db, LEGAL_FILES_REF);
    
    // Try both lawyerId and lawyerID fields
    const q1 = query(legalFilesRef, where('lawyerId', '==', lawyerID));
    const q2 = query(legalFilesRef, where('lawyerID', '==', lawyerID));
    
    const [snapshot1, snapshot2] = await Promise.all([
      getDocs(q1),
      getDocs(q2)
    ]);
    
    const legalFiles: LegalFile[] = [];
    const processedIds = new Set<string>();
    
    // Process results from lawyerId query
    snapshot1.forEach((doc) => {
      if (!processedIds.has(doc.id)) {
        legalFiles.push({
          id: doc.id,
          ...doc.data()
        } as LegalFile);
        processedIds.add(doc.id);
      }
    });
    
    // Process results from lawyerID query
    snapshot2.forEach((doc) => {
      if (!processedIds.has(doc.id)) {
        legalFiles.push({
          id: doc.id,
          ...doc.data()
        } as LegalFile);
        processedIds.add(doc.id);
      }
    });
    
    
    return legalFiles;
  } catch (error) {
    console.error('Error fetching legal files by lawyer ID:', error);
    return [];
  }
};